import { EventParticipation } from '@models/Event'
import { eventParticipationSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  deleteEventParticipation as deleteEventParticipationAPI,
  fetchEventParticipationById,
  fetchEventParticipations,
  updateEventParticipation as updateEventParticipationAPI,
} from '@services/EventParticipationService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<EventParticipation> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'eventParticipation',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getEventParticipationsSuccess,
  setQuery: setEventParticipationQuery,
  reset: resetEventParticipationState,
  resetQueryAndIds: resetEventParticipationQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: EventParticipation }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectEventParticipations = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedEventParticipation = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectEventParticipationById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectEventParticipationsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedParticipation =
  (participation: EventParticipation | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<EventParticipation>(participation)
      )
    )

export const getEventParticipations = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchEventParticipations(params),
    [eventParticipationSchema],
    slice.actions
  )

export const getEventParticipationWithId = (id: number) =>
  SBAPI.FetchDispatched<EventParticipation>(
    fetchEventParticipationById(id),
    eventParticipationSchema,
    slice.actions
  )

export const updateEventParticipation = (
  eventParticipation: EventParticipation
) =>
  SBAPI.UpdateDispatched<EventParticipation>(
    updateEventParticipationAPI(eventParticipation),
    eventParticipation,
    slice.actions
  )

export const deleteEventParticipation = (
  eventParticipation: EventParticipation
) =>
  SBAPI.DestroyDispatched<EventParticipation>(
    deleteEventParticipationAPI(eventParticipation.id),
    eventParticipation,
    slice.actions
  )
